<template>
  <topHeader />
  <div class="notice-list-page">
    <div class="container">
      <div class="page-header">
        <h2 class="page-title">系统通知</h2>
      </div>

      <div v-if="loading" class="loading-text">加载中...</div>
      <div v-else-if="noticeList.length === 0" class="empty-text">暂无通知</div>
      <div v-else class="notice-list">
        <div 
          v-for="notice in noticeList" 
          :key="notice.noticeId" 
          class="notice-item"
          :class="{ expanded: expandedNotices.includes(notice.noticeId) }"
        >
          <div class="notice-header" @click="toggleNotice(notice.noticeId)">
            <div class="notice-header-content">
              <h3 class="notice-title">{{ notice.noticeTitle || '系统通知' }}</h3>
              <div class="notice-header-meta">
                <span class="notice-time">{{ formatDate(notice.createTime || notice.readTime) }}</span>
                <button class="toggle-btn">
                  {{ expandedNotices.includes(notice.noticeId) ? '^收起' : '~展开' }}
                </button>
              </div>
            </div>
          </div>
          
          <div 
            v-show="expandedNotices.includes(notice.noticeId)" 
            class="notice-content"
          >
            <div v-if="loadingDetails[notice.noticeId]" class="loading-text">加载详情中...</div>
            <div 
              v-else
              class="notice-body" 
              v-html="getNoticeDetailContent(notice)"
            ></div>
            <!-- <div class="notice-footer">
              <span class="notice-time">{{ formatDate(notice.createTime || notice.readTime) }}</span>
              <button class="toggle-btn" @click="toggleNotice(notice.noticeId)">^收起</button>
            </div> -->
          </div>
        </div>
      </div>

      <!-- 分页 -->
      <div v-if="totalPages > 1" class="pagination">
        <button 
          class="page-btn" 
          :disabled="currentPage === 1" 
          @click="goToPage(currentPage - 1)"
        >
          上一页
        </button>
        <span class="page-info">
          第 {{ currentPage }} / {{ totalPages }} 页
        </span>
        <button 
          class="page-btn" 
          :disabled="currentPage === totalPages" 
          @click="goToPage(currentPage + 1)"
        >
          下一页
        </button>
      </div>
    </div>
  </div>
  <SiteFooter />
</template>

<script>
import topHeader from './top-Header.vue'
import SiteFooter from './Footer.vue'
import { getNoticeList, getNoticeDetail } from '@/api/user'

export default {
  name: 'NoticeList',
  components: {
    topHeader,
    SiteFooter
  },
  data() {
    return {
      noticeList: [],
      loading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      expandedNotices: [], // 存储已展开的通知ID
      noticeDetails: {}, // 存储已加载的通知详情 { noticeId: detailData }
      loadingDetails: {} // 存储正在加载的通知ID { noticeId: true/false }
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize) || 1
    }
  },
  mounted() {
    this.loadNotices()
  },
  methods: {
    async loadNotices() {
      this.loading = true
      try {
        const res = await getNoticeList({
          pageNum: this.currentPage,
          pageSize: this.pageSize
        })
        console.log('通知列表接口返回:', res)
        
        // 适配不同的返回格式
        let list = []
        let total = 0
        
        if (res) {
          // 格式1: { rows: [...], total: ... }
          if (res.rows && Array.isArray(res.rows)) {
            list = res.rows
            total = res.total || res.totalCount || 0
          }
          // 格式2: { data: { rows: [...], total: ... } }
          else if (res.data && res.data.rows && Array.isArray(res.data.rows)) {
            list = res.data.rows
            total = res.data.total || res.data.totalCount || 0
          }
          // 格式3: { data: [...] }
          else if (res.data && Array.isArray(res.data)) {
            list = res.data
            total = res.total || res.totalCount || list.length
          }
          // 格式4: { code: 200, data: [...] }
          else if (res.code === 200 && res.data) {
            if (Array.isArray(res.data)) {
              list = res.data
              total = res.total || res.totalCount || list.length
            } else if (res.data.rows && Array.isArray(res.data.rows)) {
              list = res.data.rows
              total = res.data.total || res.data.totalCount || 0
            }
          }
          // 格式5: 直接返回数组
          else if (Array.isArray(res)) {
            list = res
            total = res.length
          }
        }
        
        this.noticeList = list || []
        this.total = total || 0
        
        // 默认展开第一条通知
        if (this.noticeList.length > 0 && this.expandedNotices.length === 0) {
          const firstNoticeId = this.noticeList[0].noticeId || this.noticeList[0].id
          this.expandedNotices.push(firstNoticeId)
          // 加载第一条通知的详情
          if (firstNoticeId && !this.noticeDetails[firstNoticeId] && !this.loadingDetails[firstNoticeId]) {
            this.loadNoticeDetail(firstNoticeId)
          }
        }
      } catch (error) {
        console.error('获取通知列表失败:', error)
        this.noticeList = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    async toggleNotice(noticeId) {
      const index = this.expandedNotices.indexOf(noticeId)
      if (index > -1) {
        // 如果已展开，则收起
        this.expandedNotices.splice(index, 1)
      } else {
        // 如果未展开，则展开并加载详情
        this.expandedNotices.push(noticeId)
        // 如果还没有加载过详情，则调用详情接口
        if (!this.noticeDetails[noticeId] && !this.loadingDetails[noticeId]) {
          await this.loadNoticeDetail(noticeId)
        }
      }
    },
    async loadNoticeDetail(noticeId) {
      if (!noticeId) return
      
      // 设置加载状态
      this.loadingDetails[noticeId] = true
      
      try {
        const res = await getNoticeDetail(noticeId)
        console.log('通知详情接口返回:', res)
        
        // 适配不同的返回格式
        let detail = null
        if (res) {
          // 格式1: { code: 200, data: {...} } - 优先检查带 code 的格式
          if (res.code === 200 && res.data) {
            detail = res.data
          }
          // 格式2: { data: {...} } - 没有 code 字段，但有 data
          else if (res.data) {
            detail = res.data
          }
          // 格式3: 直接返回详情对象
          else if (res.noticeContent || res.content || res.noticeTitle || res.title) {
            detail = res
          }
        }
        
        // 存储详情数据
        if (detail) {
          this.noticeDetails[noticeId] = detail
          
          // 更新列表中的通知数据
          const noticeIndex = this.noticeList.findIndex(n => (n.noticeId || n.id) === noticeId)
          if (noticeIndex > -1) {
            // 合并详情数据到列表项
            this.noticeList[noticeIndex] = {
              ...this.noticeList[noticeIndex],
              ...detail
            }
          }
        }
      } catch (error) {
        console.error('获取通知详情失败:', error)
      } finally {
        // 清除加载状态
        this.loadingDetails[noticeId] = false
      }
    },
    getNoticeDetailContent(notice) {
      const noticeId = notice.noticeId || notice.id
      const detail = this.noticeDetails[noticeId]
      
      // 优先使用详情数据，其次使用列表数据
      if (detail) {
        return detail.noticeContent || detail.content || ''
      }
      return notice.noticeContent || notice.content || ''
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
        this.expandedNotices = [] // 切换页面时清空展开状态
        this.noticeDetails = {} // 清空详情数据
        this.loadingDetails = {} // 清空加载状态
        this.loadNotices()
        // 滚动到顶部
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = date.getHours()
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      
      // 判断上午/下午
      const period = hours >= 12 ? '下午' : '上午'
      const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours)
      const displayHoursStr = String(displayHours).padStart(2, '0')
      
      return `${year}/${month}/${day} ${period}${displayHoursStr}:${minutes}:${seconds}`
    }
  }
}
</script>

<style scoped>
.notice-list-page {
  min-height: 500px;
  background-color: #f5f5f5;
  padding: 20px 0;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.page-header {
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 2px solid #422C27;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #422C27;
  margin: 0;
}

.loading-text,
.empty-text {
  text-align: center;
  color: #999;
  padding: 40px 0;
  font-size: 14px;
}

.notice-content .loading-text {
  padding: 20px 0;
  font-size: 14px;
  color: #666;
}

.notice-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.notice-item {
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.3s;
}

.notice-item:last-child {
  border-bottom: none;
}

.notice-item:hover {
  background-color: #fafafa;
}

.notice-header {
  cursor: pointer;
  padding: 15px 20px;
  transition: background-color 0.3s;
}

.notice-header:hover {
  background-color: #f5f5f5;
}

.notice-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
}

.notice-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin: 0;
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notice-header-meta {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-shrink: 0;
}

.notice-time {
  font-size: 13px;
  color: #999;
  white-space: nowrap;
}

.toggle-btn {
  background: none;
  border: none;
  color: #422C27;
  font-size: 13px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.3s;
  white-space: nowrap;
}

.toggle-btn:hover {
  background-color: rgba(66, 44, 39, 0.1);
}

.notice-content {
  padding: 0 20px 15px 20px;
  background-color: #fafafa;
  border-top: 1px solid #f0f0f0;
}

.notice-body {
  padding: 15px 0;
  font-size: 14px;
  color: #666;
  line-height: 1.8;
  word-wrap: break-word;
}

.notice-body :deep(p) {
  margin: 0 0 10px 0;
}

.notice-body :deep(a) {
  color: #422C27;
  text-decoration: none;
}

.notice-body :deep(a:hover) {
  text-decoration: underline;
}

.notice-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 10px;
  border-top: 1px solid #e0e0e0;
  margin-top: 15px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 30px;
  padding: 20px 0;
}

.page-btn {
  padding: 8px 20px;
  background: #422C27;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
}

.page-btn:hover:not(:disabled) {
  background: #0d6b5f;
}

.page-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.page-info {
  font-size: 14px;
  color: #666;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .notice-header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .notice-header-meta {
    width: 100%;
    justify-content: space-between;
  }

  .notice-title {
    white-space: normal;
    text-overflow: unset;
  }

  .notice-content {
    padding: 0 15px 15px 15px;
  }

  .pagination {
    flex-direction: column;
    gap: 10px;
  }
}
</style>
